import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../../shared/components/Button';
import { InlineAlert } from '../../../shared/components/InlineAlert';
import { describeError } from '../describeError';
import { mappingMethodLabel } from '../format';
import { useAsyncData } from '../hooks/useAsyncData';
import { useValidationSession } from '../hooks/useValidationSession';
import { payrollHistoryImportGateway as gateway } from '../index';
import { batchStepPath, usesGuidedMapping } from '../routes';
import type { BatchDetail, Capabilities, EmployeeMappingRow } from '../types';
import { ErrorSummary } from './basics';
import { IssueGroups } from './IssueGroups';

interface PickerOption {
  id: string;
  label: string;
}

const labelFor = (o: { employeeNumber: string; displayName: string; isActive?: boolean }): string =>
  `${o.displayName} (${o.employeeNumber})${o.isActive === false ? ' - inactive' : ''}`;

function MappingTable({
  batchId, rows, canEdit, saving, onSave,
}: {
  batchId: string;
  rows: EmployeeMappingRow[];
  canEdit: boolean;
  saving: boolean;
  onSave: (selections: Record<string, string>) => Promise<boolean>;
}) {
  const needsPicker = canEdit && rows.some((r) => r.requiresMapping);
  const [searchText, setSearchText] = useState('');
  const [search, setSearch] = useState('');
  const [selections, setSelections] = useState<Record<string, string>>({});
  const [chosenLabels, setChosenLabels] = useState<Record<string, string>>({});

  // Debounce: the typed text becomes the query shortly after typing stops (set from a timer callback, not
  // synchronously inside the effect).
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchText), 250);
    return () => clearTimeout(timer);
  }, [searchText]);

  const loadOptions = useCallback(
    (signal: AbortSignal) => (needsPicker ? gateway.searchEmployeeOptions(batchId, search, signal) : Promise.resolve([])),
    [batchId, search, needsPicker],
  );
  const options = useAsyncData(loadOptions);

  const optionList = useMemo(() => {
    const map = new Map<string, PickerOption>();
    for (const o of options.data ?? []) map.set(o.epnEmployeeId, { id: o.epnEmployeeId, label: labelFor(o) });
    for (const r of rows) {
      if (r.suggestion) {
        map.set(r.suggestion.epnEmployeeId, { id: r.suggestion.epnEmployeeId, label: labelFor({ employeeNumber: r.suggestion.epnEmployeeNumber, displayName: r.suggestion.epnEmployeeName }) });
      }
    }
    for (const [id, label] of Object.entries(chosenLabels)) if (!map.has(id)) map.set(id, { id, label });
    return [...map.values()];
  }, [options.data, rows, chosenLabels]);

  const choose = (sourceEmployeeId: string, epnEmployeeId: string, label?: string) => {
    setSelections((prev) => {
      const next = { ...prev };
      if (epnEmployeeId) next[sourceEmployeeId] = epnEmployeeId;
      else delete next[sourceEmployeeId];
      return next;
    });
    if (epnEmployeeId) {
      const found = label ?? optionList.find((o) => o.id === epnEmployeeId)?.label;
      if (found) setChosenLabels((prev) => ({ ...prev, [epnEmployeeId]: found }));
    }
  };

  const selectedCount = Object.keys(selections).length;

  return (
    <section aria-labelledby="phi-mapping-heading">
      <h3 id="phi-mapping-heading">Employee mapping</h3>
      <p className="muted">EPN never matches employees by name alone. A person is matched by an ID in your file, a mapping you saved, or a choice you make here.</p>
      {needsPicker && (
        <div className="phi-field">
          <label htmlFor="phi-employee-search">Search Escochex employees</label>
          <input
            id="phi-employee-search"
            type="search"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Name or employee number"
          />
          {options.error !== undefined && <span className="error">Could not search employees. Try again.</span>}
        </div>
      )}
      <div className="table-scroll">
        <table>
          <caption>Employees in the package and the Escochex employees they are matched to</caption>
          <thead>
            <tr>
              <th scope="col">Employee in package</th>
              <th scope="col">Email</th>
              <th scope="col">Employment status</th>
              <th scope="col">Match</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.sourceEmployeeId}>
                <th scope="row">
                  {r.displayName}
                  <div className="muted">{r.employeeNumber ?? r.sourceEmployeeId}</div>
                </th>
                <td>{r.email ?? '—'}</td>
                <td>{r.employmentStatus}</td>
                <td>
                  {r.mapping ? (
                    <span>
                      <span aria-hidden="true">✓ </span>
                      Matched to {r.mapping.epnEmployeeName} ({r.mapping.epnEmployeeNumber})
                      {!r.mapping.epnEmployeeActive && ' - inactive employee'}
                      <span className="muted"> · {mappingMethodLabel(r.mapping.method)}</span>
                    </span>
                  ) : canEdit ? (
                    <div>
                      <p><span aria-hidden="true">! </span>Needs a match</p>
                      <select
                        className="phi-select phi-map-select"
                        aria-label={`Escochex employee for ${r.displayName}`}
                        value={selections[r.sourceEmployeeId] ?? ''}
                        onChange={(e) => choose(r.sourceEmployeeId, e.target.value)}
                      >
                        <option value="">Select an employee…</option>
                        {optionList.map((o) => (
                          <option key={o.id} value={o.id}>{o.label}</option>
                        ))}
                      </select>
                      {r.suggestion && (
                        <p>
                          <span className="muted">
                            Suggested by email: {r.suggestion.epnEmployeeName} ({r.suggestion.epnEmployeeNumber}).{' '}
                          </span>
                          <button
                            type="button"
                            className="phi-btn-secondary"
                            aria-label={`Apply suggestion for ${r.displayName}: ${r.suggestion.epnEmployeeName}`}
                            onClick={() =>
                              r.suggestion &&
                              choose(r.sourceEmployeeId, r.suggestion.epnEmployeeId, labelFor({ employeeNumber: r.suggestion.epnEmployeeNumber, displayName: r.suggestion.epnEmployeeName }))
                            }
                          >
                            Apply suggestion
                          </button>
                        </p>
                      )}
                    </div>
                  ) : (
                    <span><span aria-hidden="true">! </span>Needs a match (requires permission to change imports)</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {canEdit && rows.some((r) => r.requiresMapping) && (
        <div className="phi-actions">
          <Button type="button" disabled={selectedCount === 0} loading={saving} loadingLabel="Saving mappings…" onClick={() => void onSave(selections).then((ok) => { if (ok) setSelections({}); })}>
            Save mappings
          </Button>
          <span className="muted">Nothing is matched until you choose an employee and save. Suggestions are never applied automatically.</span>
        </div>
      )}
    </section>
  );
}

interface Props {
  batch: BatchDetail;
  capabilities: Capabilities;
  announce: (message: string) => void;
  refreshBatch: () => Promise<void>;
}

/** Step 5: match the people in the file to EPN employees. A failed batch also lands here, with its blocking errors
 * and the ways back: fix the problems, or (guided mapping) change the column mapping. */
export function EmployeesStep({ batch, capabilities, announce, refreshBatch }: Props) {
  const session = useValidationSession(batch.id, announce, refreshBatch);
  const { loading, data, error, reload, busy, actionError, apply } = session;
  const guided = usesGuidedMapping(batch);

  const save = (selections: Record<string, string>) =>
    apply(
      'save',
      () => gateway.saveMappings(batch.id, { mappings: Object.entries(selections).map(([sourceEmployeeId, epnEmployeeId]) => ({ sourceEmployeeId, epnEmployeeId })) }),
      (s) => (s.status === 'ReadyForReview' ? 'Mappings saved. The import is ready for review.' : `Mappings saved. ${s.employeesRequiringMapping} employees still need a match.`),
    );

  if (loading) return <p className="muted" role="status">Loading validation results…</p>;
  if (error !== undefined || !data) {
    return (
      <div>
        <ErrorSummary message={describeError(error, 'Could not load the validation results.')} />
        <button type="button" className="phi-btn-secondary" onClick={reload}>Try again</button>
      </div>
    );
  }

  const s = data;
  const ready = s.status === 'ReadyForReview';
  const validationFailed = s.status === 'ValidationFailed';
  // Not fixable by matching employees: the file or (guided) the column mapping has to change.
  const failed = validationFailed && !s.canFixByMapping;
  const canEdit = capabilities.canCreate && (ready || (validationFailed && s.canFixByMapping));
  const nextHintId = 'phi-next-hint';

  return (
    <div>
      {actionError && <ErrorSummary message={actionError} />}
      <p>EPN matches each person in your file to an employee in your company so their history lands on the right record.</p>
      {failed && (
        <InlineAlert>
          {guided
            ? 'This file has problems, so it cannot be imported yet. Open Fix problems to see what to correct, or change the column mapping.'
            : 'This package failed validation and cannot be imported. Fix the blocking errors in your export, cancel this import, and upload a corrected package.'}
        </InlineAlert>
      )}
      {validationFailed && s.canFixByMapping && (
        <div className="phi-notice">
          <p>Some employees in the package are not matched to Escochex employees yet.</p>
          <p className="muted">Match each one in the Employee mapping table and save. The import is revalidated automatically.</p>
        </div>
      )}
      {(batch.status === 'ValidationFailed' || batch.status === 'Failed') && batch.failureReason && (
        <InlineAlert>{batch.failureReason}</InlineAlert>
      )}

      <dl className="phi-summary" aria-label="Employee matches">
        <div><dt>Employees detected</dt><dd>{s.employeesDetected}</dd></div>
        <div><dt>Employees matched</dt><dd>{s.employeesMatched}</dd></div>
        <div><dt>Still need a match</dt><dd>{s.employeesRequiringMapping}</dd></div>
      </dl>

      <IssueGroups issues={s.issues} truncated={s.issuesTruncated} only={['BlockingError']} />

      {validationFailed && (
        <div className="phi-actions">
          <Link to={batchStepPath(batch.id, 'problems')} className="phi-btn-secondary">Fix problems</Link>
          {guided && capabilities.canCreate && (
            <Link to={batchStepPath(batch.id, 'map')} className="phi-btn-secondary">Change column mapping</Link>
          )}
        </div>
      )}

      {!failed && <MappingTable batchId={batch.id} rows={s.employees} canEdit={canEdit} saving={busy === 'save'} onSave={save} />}

      <div className="phi-footer">
        <Link to={batchStepPath(batch.id, guided ? 'map' : 'found')} className="phi-btn-secondary">Back</Link>
        <div className="phi-footer-group">
          {guided && ready && capabilities.canCreate && (
            <Link to={batchStepPath(batch.id, 'map')} className="phi-btn-secondary">Change column mapping</Link>
          )}
          {!ready && (
            <span id={nextHintId} className="muted">
              {failed ? 'This import cannot continue until the problems are fixed.' : 'Resolve every blocking error (match all employees) to continue.'}
            </span>
          )}
          {ready ? (
            <Link to={batchStepPath(batch.id, 'totals')} className="phi-btn-primary">Next: Review totals</Link>
          ) : (
            <button type="button" disabled aria-describedby={nextHintId}>
              Next: Review totals
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
